import { composeRefs } from '@radix-ui/react-compose-refs'
import classNames from 'classnames'
import React, { forwardRef, useMemo, useRef } from 'react'

import styles from './chat-input-shell.module.scss'
import { type ChatInputShellContextValue, ChatInputShellContext } from './chat-input-shell-context'
import { useIsomorphicLayoutEffect } from './use-isomorphic-layout-effect'

export interface ChatInputShellFormProps {
  children: React.ReactNode
  className?: string
  disabled?: boolean
  busy?: boolean
  onSubmit?: (evt: React.FormEvent<HTMLFormElement>) => void
}

export const ChatInputShellForm = forwardRef<HTMLFormElement, ChatInputShellFormProps>(
  function ChatInputShellForm(props, forwardedRef) {
    const { children, className, disabled = false, busy = false, onSubmit, ...rest } = props

    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const formRef = useRef<HTMLFormElement>(null)
    const submitRef = useRef<HTMLButtonElement>(null)

    const formClasses = classNames(styles.form, { [styles.disabled]: disabled }, className)

    const ctxValue = useMemo<ChatInputShellContextValue>(
      () => ({ textareaRef, formRef, submitRef, busy, disabled }),
      [busy, disabled]
    )

    useIsomorphicLayoutEffect(() => {
      const container = formRef.current
      if (!container) return

      function onClick(e: MouseEvent) {
        if (e.target instanceof HTMLButtonElement || e.target instanceof HTMLAnchorElement) {
          return
        }
        textareaRef.current?.focus()
      }

      container.addEventListener('click', onClick)
      return () => {
        container.removeEventListener('click', onClick)
      }
    }, [])

    return (
      <ChatInputShellContext.Provider value={ctxValue}>
        <form
          {...rest}
          onSubmit={onSubmit}
          className={formClasses}
          ref={composeRefs(forwardedRef, formRef)}
        >
          {children}
        </form>
      </ChatInputShellContext.Provider>
    )
  }
)
